import { createContext, useContext, useState } from 'react'
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ConsoleError, type ConsoleApi, type RegionList, type SessionState } from './types'
import { catchingUp, missing } from '@/lib/regions'
import { useConsole } from '@/lib/store'

export const ApiContext = createContext<ConsoleApi | null>(null)

export function useApi(): ConsoleApi {
  const api = useContext(ApiContext)
  if (!api) throw new Error('no console API')
  return api
}

export const keys = {
  session: ['session'] as const,
  shares: ['shares'] as const,
  devices: ['devices'] as const,
  browsers: ['browsers'] as const,
  regions: ['regions'] as const,
}

// Only an unreachable Purlview is worth asking again at once; a refusal is an
// answer.
export function retryable(error: unknown): boolean {
  return error instanceof ConsoleError && (error.code === 'network' || error.code === 'unavailable')
}

export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (count, error) => count < 2 && retryable(error),
        retryDelay: (n) => Math.min(4000, 800 * 2 ** n),
        refetchOnWindowFocus: true,
        staleTime: 2000,
      },
      mutations: { retry: false },
    },
  })
}

/** The refresh (D-014): every 5 seconds while the page is visible, held while a confirmation is open. */
export const refreshMs = 5000

function useRefresh() {
  const holds = useConsole((s) => s.holds)
  return holds > 0 ? false : refreshMs
}

export function useSession() {
  const api = useApi()
  const setServerNow = useConsole((s) => s.setServerNow)
  return useQuery({
    queryKey: keys.session,
    queryFn: async () => {
      const s = await api.session()
      setServerNow(s.now)
      return s
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

/** A list call that finds the session gone sends the console back to sign-in. */
function useSignedOutGuard() {
  const client = useQueryClient()
  return (error: unknown) => {
    if (error instanceof ConsoleError && error.code === 'signed_out') {
      void client.invalidateQueries({ queryKey: keys.session })
    }
  }
}

function useList<T extends { now: string }>(key: readonly string[], fetch: () => Promise<T>) {
  const refetchInterval = useRefresh()
  const guard = useSignedOutGuard()
  const setServerNow = useConsole((s) => s.setServerNow)
  return useQuery({
    queryKey: key,
    queryFn: async () => {
      try {
        const out = await fetch()
        setServerNow(out.now)
        return out
      } catch (e) {
        guard(e)
        throw e
      }
    },
    refetchInterval,
    refetchIntervalInBackground: false,
  })
}

export function useShares() {
  const api = useApi()
  return useList(keys.shares, api.shares)
}

export function useDevices() {
  const api = useApi()
  return useList(keys.devices, api.devices)
}

export function useBrowsers() {
  const api = useApi()
  return useList(keys.browsers, api.browsers)
}

/**
 * How long the console waits to read the leaderboard again while it does not
 * show this account's votes yet: just past the 10 seconds the account host
 * reuses the shares for (cacheFor in
 * platform/controlplane/internal/regions/board.go), so the next answer has
 * the vote in it.
 */
export const catchUpMs = 11_000

/** How many reads the leaderboard gets while it is missing the same votes before the console stops asking: about a minute's worth. */
export const catchUpTries = 6

/**
 * The leaderboard, read once per visit: shares move slowly, and the account
 * host caches them anyway. While a region this account voted for shows no
 * share (a vote's answer, or a visit within the cache's life of voting) it
 * is read again every catchUpMs, until it shows the vote or catchUpTries
 * reads have not; then late is true and the console stops asking until the
 * next vote or visit.
 */
export function useRegions() {
  const api = useApi()
  const client = useQueryClient()
  const guard = useSignedOutGuard()
  const setServerNow = useConsole((s) => s.setServerNow)
  // The reads, answered or failed, made while the board was missing these
  // votes; a board that shows them, or a new vote, starts the count again.
  const [tries, setTries] = useState({ missing: '', n: 0 })
  const count = (board: RegionList | undefined) => {
    const now = missing(board?.regions).join()
    setTries((t) => (now === '' ? { missing: '', n: 0 } : { missing: now, n: t.missing === now ? t.n + 1 : 1 }))
  }
  const late = (board: RegionList | undefined) => tries.n >= catchUpTries && tries.missing === missing(board?.regions).join()
  const query = useQuery({
    queryKey: keys.regions,
    queryFn: async () => {
      try {
        const out = await api.regions()
        setServerNow(out.now)
        count(out)
        return out
      } catch (e) {
        count(client.getQueryData<RegionList>(keys.regions))
        guard(e)
        throw e
      }
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => (catchingUp(q.state.data?.regions) && !late(q.state.data) ? catchUpMs : false),
  })
  return { board: query, late: late(query.data) }
}

/**
 * A vote: the answer is the leaderboard as the account host has it cached,
 * with the vote marked; it is shown as it is, never adjusted here, so a vote
 * is never counted twice on screen. useRegions reads the leaderboard again
 * until it shows the vote.
 */
export function useVote() {
  const api = useApi()
  const client = useQueryClient()
  const session = useSession().data
  const guard = useSignedOutGuard()
  return useMutation({
    mutationFn: (region: string) => api.voteRegion(csrfOf(session), region),
    onError: guard,
    onSuccess: (answer) => {
      client.setQueryData<RegionList>(keys.regions, answer)
    },
  })
}

export function csrfOf(session: SessionState | undefined): string {
  return session?.csrf ?? ''
}

/** A state change as the signed-in account: its token, then every list refreshed. */
export function useAction<A>(run: (api: ConsoleApi, csrf: string, arg: A) => Promise<void>) {
  const api = useApi()
  const client = useQueryClient()
  const session = useSession().data
  const guard = useSignedOutGuard()
  return useMutation({
    mutationFn: (arg: A) => run(api, csrfOf(session), arg),
    onError: guard,
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.shares })
      void client.invalidateQueries({ queryKey: keys.devices })
      void client.invalidateQueries({ queryKey: keys.browsers })
    },
  })
}
